import { useState } from "react";
import { TopMenu } from "../../top-menu/TopMenu";
import { Footer } from "../../footer/Footer";
import { PageHero } from "../../page-hero/PageHero";
import { Field, Select, Textarea, FormSection } from "../../form-fields/FormFields";

const PROJECT_TYPES = ['Commercial', 'Industrial', 'Public Works / Education', 'Misc. Metals / Architectural', 'Other'];
const TONNAGE_RANGES = ['Under 100 tons', '100 – 500 tons', '500 – 1,500 tons', '1,500+ tons', 'Not sure yet'];
const SOFTWARE_OPTIONS = ['Tekla PowerFab', 'FabSuite', 'Steel Projects', 'Other / None'];

export const ContactUs = () => {
    const [formData, setFormData] = useState({
        name: '', company: '', email: '', phone: '',
        projectType: '', tonnage: '', deadline: '', software: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
    };

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <TopMenu />

            <PageHero
                eyebrow="Get a Quote"
                title="Let's talk"
                accent="about your project."
                subtitle="Tell us a few details and we'll respond with scope, timeline, and pricing within 48 hours. For complex projects, expect a quick call to align on the deliverables."
            />

            <main className="flex-1">
                <section className="py-20 md:py-28">
                    <div className="container-px mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
                        <div className="lg:col-span-2 lg:sticky lg:top-28">
                            <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Talk to us directly</p>
                            <h2 className="text-display-md font-display text-navy-900 mb-6">
                                Prefer a call or email?
                            </h2>
                            <p className="text-steel-600 leading-relaxed mb-10">
                                Pick up the phone or send a quick email — we read every message that lands in our inbox and respond fast.
                            </p>

                            <div className="space-y-6">
                                <ContactCard
                                    icon={<PhoneIcon />}
                                    label="Call Jim — CEO"
                                    primary={{ type: 'tel', value: '(858) 344-2140' }}
                                />
                                <ContactCard
                                    icon={<MailIcon />}
                                    label="Email"
                                    secondary={[
                                        { type: 'mail', value: 'jim@aircad.net' },
                                        { type: 'mail', value: 'jim@rockypointdetailing.com' },
                                    ]}
                                />
                                <ContactCard
                                    icon={<MapIcon />}
                                    label="Office"
                                    text={['1605 Palmales Ct.', 'Las Vegas, NV 89128']}
                                    secondary={[
                                        { type: 'tel', value: '(702) 430-8892', display: '(702) 430-8892 · Office' },
                                        { type: 'tel', value: '(858) 324-1903', display: '(858) 324-1903 · Direct' },
                                    ]}
                                />
                            </div>

                            <div className="mt-10 p-6 rounded-xl bg-steel-50 border border-steel-100">
                                <p className="text-sm font-semibold text-navy-900 mb-2 flex items-center gap-2">
                                    <ClockIcon />
                                    Response time
                                </p>
                                <p className="text-sm text-steel-600 m-0">
                                    We respond to quote requests within <strong className="text-navy-900">48 hours</strong>,
                                    and usually much sooner during business hours.
                                </p>
                            </div>
                        </div>

                        <div className="lg:col-span-3">
                            <div className="bg-white rounded-2xl shadow-card border border-steel-100 p-6 md:p-10">
                                {submitted ? (
                                    <div className="text-center py-16">
                                        <div className="w-16 h-16 rounded-full bg-accent/20 text-navy-700 flex items-center justify-center mx-auto mb-5">
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </div>
                                        <h3 className="text-2xl font-bold text-navy-900 font-display mb-2">Thanks — we'll be in touch.</h3>
                                        <p className="text-steel-600">We'll respond within 48 hours with next steps.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        <FormSection title="Your details">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                                <Field label="Your name" name="name" value={formData.name} onChange={handleChange} required />
                                                <Field label="Company" name="company" value={formData.company} onChange={handleChange} />
                                                <Field label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required />
                                                <Field label="Phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
                                            </div>
                                        </FormSection>

                                        <FormSection title="About the project">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                                <Select label="Project type" name="projectType" value={formData.projectType} onChange={handleChange} options={PROJECT_TYPES} required />
                                                <Select label="Approx. tonnage" name="tonnage" value={formData.tonnage} onChange={handleChange} options={TONNAGE_RANGES} />
                                                <Field label="Target start / deadline" name="deadline" value={formData.deadline} onChange={handleChange} placeholder="e.g. Q3 2026, ASAP, flexible" />
                                                <Select label="Fab software" name="software" value={formData.software} onChange={handleChange} options={SOFTWARE_OPTIONS} />
                                            </div>
                                        </FormSection>

                                        <Textarea
                                            label="Project details"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            rows={5}
                                            placeholder="Brief scope, location, special requirements, anything you'd like us to know."
                                        />

                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                                            <p className="text-xs text-steel-500 max-w-xs">
                                                By submitting, you agree to be contacted about your project inquiry.
                                            </p>
                                            <button
                                                type="submit"
                                                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-signal text-signal-ink font-semibold no-underline hover:bg-signal-hover transition-all shadow-card hover:shadow-card-hover w-full sm:w-auto"
                                            >
                                                Send request
                                                <Arrow />
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

const ContactCard = ({ icon, label, primary, secondary, text }) => (
    <div className="flex gap-4">
        <div className="w-12 h-12 rounded-xl bg-navy-900 text-accent flex items-center justify-center shrink-0">
            {icon}
        </div>
        <div className="min-w-0">
            <p className="text-xs font-semibold text-steel-500 uppercase tracking-wider mb-1.5">{label}</p>
            {primary && (
                <a href={`${primary.type}:${primary.value.replace(/[^\d+@.]/g, '')}`} className="block text-navy-900 font-bold text-lg no-underline hover:text-navy-600 transition-colors">{primary.value}</a>
            )}
            {text && text.map((t, i) => <p key={i} className="text-navy-900 font-medium m-0">{t}</p>)}
            {secondary && (
                <div className="mt-1 space-y-0.5">
                    {secondary.map((line, i) => (
                        <a
                            key={i}
                            href={`${line.type}:${line.type === 'mail' ? line.value : line.value.replace(/[^\d+]/g, '')}`}
                            className="block text-sm text-steel-600 no-underline hover:text-navy-600 transition-colors break-all"
                        >
                            {line.display || line.value}
                        </a>
                    ))}
                </div>
            )}
        </div>
    </div>
);

const PhoneIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
);

const MailIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);

const MapIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

const ClockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2A5BA8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const Arrow = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);
